/**
 * Content generation's quality checks (QC) and item failures for one catalog batch, pure: the
 * vocabulary (severities, checks, subjects) with locale labels and design-token tones, the
 * per-check fold of `/qc-summary`, and the URL search of the Quality checks detail page
 * (`/catalog-v2/runs/$runId/quality-checks`). The stage pane only ever shows these totals; every
 * finding / failure row lives on the detail page, paged on the server.
 */
import type { TFunction } from 'i18next';
import { z } from 'zod';

import type { QcSummaryRead } from '~/features/content/types/attribution.types';
import {
  QC_SEVERITIES,
  qcCheckSummaries,
  type QcSeverity,
  severityCode,
} from '~/features/content/utils/qc-flags-review';

import { humanizeCode } from './run-format';

export { QC_SEVERITIES, type QcSeverity, severityCode };

/** Content-gen `QcCheck` — the only values `qc-findings?checkName=` accepts (anything else 422s). */
export const QC_CHECKS = [
  'UNSUPPORTED_CLAIM',
  'INPUT_CONFLICT',
  'CONTRADICTION',
  'FEED_MISMATCH',
  'VOCABULARY_GAP',
  'POSSIBLY_MISSED',
  'DETECTION_DOUBT',
  'LISTING_SHAPE_MISMATCH',
  'MISSING_PIECE',
  'PACK_COLOURS',
  'NO_ANSWERS',
] as const;

/** The detail page's tabs — findings, R16's message patterns, item failures. */
export const QUALITY_CHECK_TABS = ['findings', 'patterns', 'failures'] as const;
export type QualityCheckTab = (typeof QUALITY_CHECK_TABS)[number];

/** Findings per page of the detail page (content-gen allows ≤ 500; ⑨'s title join caps at 100). */
export const QC_FINDINGS_PAGE_SIZE = 100;
/** Failures per keyset page of the detail page (content-gen allows ≤ 200). */
export const QC_FAILURES_PAGE_SIZE = 100;
/** Most product ids one `qc-findings` read may filter by (content-gen `_MAX_PRODUCT_IDS`). */
export const QC_MAX_PRODUCT_FILTER = 100;

/** Severity → design tokens: the bar/legend fill, the per-check dot and the row chip. */
export const QC_SEVERITY_TONE: Record<QcSeverity, { fill: string; chip: string }> = {
  HIGH: { fill: 'bg-error', chip: 'border-error/30 bg-error-50 text-error' },
  MEDIUM: { fill: 'bg-warning', chip: 'border-warning/30 bg-warning-50 text-warning' },
  SUGGESTION: { fill: 'bg-warning/60', chip: 'border-warning/20 bg-warning-50/60 text-warning' },
  INFO: { fill: 'bg-muted-foreground', chip: 'border-border bg-muted text-muted-foreground' },
};

type ContentGenT = TFunction<'catalog-v2-content-gen'>;

/** A QC check code in words ("Unsupported claim"); an unknown code falls back to its humanised form. */
export function qcCheckLabel(t: ContentGenT, code: string): string {
  return t(`qcLabels.check.${code}`, { defaultValue: humanizeCode(code) });
}

/** A QC severity code in words ("High", "Suggestion"). */
export function qcSeverityLabel(t: ContentGenT, code: string): string {
  return t(`qcLabels.severity.${severityCode(code)}`);
}

/** What a finding is about ("Attribute", "Product type"); unknown codes are humanised. */
export function qcSubjectLabel(t: ContentGenT, code: string): string {
  return t(`qcLabels.subject.${code}`, { defaultValue: humanizeCode(code) });
}

/** One "By check" row: findings and affected products (the largest severity row — a floor), worst severity. */
export interface QcCheckRow {
  checkName: string;
  findings: number;
  products: number;
  worst: QcSeverity;
}

/** Folds `/qc-summary` (check × severity) into one row per check, largest first. */
export function qcCheckRows(summary: readonly QcSummaryRead[]): QcCheckRow[] {
  return qcCheckSummaries(summary)
    .map((c) => ({
      checkName: c.checkName,
      findings: c.flags,
      products: c.products,
      worst: QC_SEVERITIES.find((s) => c.bySeverity[s] > 0) ?? 'INFO',
    }))
    .sort((a, b) => b.findings - a.findings);
}

/** Findings per severity from `/qc-summary` — the detail page's severity dropdown counts. */
export function qcSeverityTotals(summary: readonly QcSummaryRead[]): Record<QcSeverity, number> {
  const totals: Record<QcSeverity, number> = { HIGH: 0, MEDIUM: 0, SUGGESTION: 0, INFO: 0 };
  for (const row of summary) totals[severityCode(row.severity)] += row.findings;
  return totals;
}

/** One pattern piece: literal text, or a placeholder the server took a value / number out of. */
export interface QcPatternPart {
  text: string;
  placeholder: boolean;
}

/** Splits a R16 pattern on its `<value>` / `<n>` placeholders so the UI can mark them. */
export function qcPatternParts(pattern: string): QcPatternPart[] {
  return pattern
    .split(/(<value>|<n>)/g)
    .filter((part) => part !== '')
    .map((part) => ({ text: part, placeholder: part === '<value>' || part === '<n>' }));
}

/** True for a check code `qc-findings?checkName=` accepts (a pattern row can then filter by it). */
export function isKnownQcCheck(code: string): code is (typeof QC_CHECKS)[number] {
  return (QC_CHECKS as readonly string[]).includes(code);
}

/** `failuresByReason` as rows, largest first (zero counts dropped). */
export function failureReasonRows(
  byReason: Record<string, number> | null | undefined,
): { reason: string; count: number }[] {
  return Object.entries(byReason ?? {})
    .filter(([, count]) => count > 0)
    .map(([reason, count]) => ({ reason, count }))
    .sort((a, b) => b.count - a.count);
}

/**
 * URL search of the Quality checks detail page — every field falls back to a safe default on a
 * bad value (`.catch`), so a stale or hand-edited link still lands on a working page. `cgRun` is
 * the content-gen run the pane card read (else the page resolves the batch's enrichment run);
 * `cursor` + `trail` (the cursors of the pages before it) keep the failures keyset position.
 */
export const qualityChecksSearchSchema = z.object({
  tab: z.enum(QUALITY_CHECK_TABS).catch('findings'),
  cgRun: z.string().max(200).optional().catch(undefined),
  severity: z.enum(QC_SEVERITIES).optional().catch(undefined),
  check: z.enum(QC_CHECKS).optional().catch(undefined),
  products: z
    .array(z.string().min(1).max(200))
    .max(QC_MAX_PRODUCT_FILTER)
    .optional()
    .catch(undefined),
  page: z.number().int().min(1).optional().catch(undefined),
  product: z.string().max(200).optional().catch(undefined),
  cursor: z.string().max(2000).optional().catch(undefined),
  trail: z.array(z.string().max(2000)).max(200).optional().catch(undefined),
});
export type QualityChecksSearch = z.infer<typeof qualityChecksSearchSchema>;
